<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Support Tickets</h5>
        <p class="page-sub">Raise and track issues with the application team</p>
      </div>
      <div class="page-actions">
        <input type="text" class="filter-input" placeholder="Search tickets…" v-model="search" @input="fetchTickets()" />
        <select v-model="filterStatus" @change="fetchTickets()" class="filter-select">
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="awaiting_user">Awaiting User</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <button class="btn-primary-action" @click="openCreateModal()" v-if="permission?.manage_ticket">
          <font-awesome-icon icon="plus" /> New Ticket
        </button>
      </div>
    </div>

    <!-- Ticket List -->
    <div class="card">
      <div class="card-body p-0">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
        </div>
        <div v-else-if="tickets.length === 0" class="empty-state">
          <div class="empty-icon-wrap">
            <font-awesome-icon icon="ticket-alt" class="empty-icon" />
          </div>
          <p>No tickets found. Raise a ticket to get support.</p>
        </div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Subject</th>
              <th>Category</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Messages</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in tickets" :key="t.id">
              <td><span class="ref-chip">{{ t.reference }}</span></td>
              <td>{{ t.subject }}</td>
              <td><span class="badge-cat" :class="'cat-' + t.category">{{ formatCategory(t.category) }}</span></td>
              <td><span class="badge-priority" :class="'pri-' + t.priority">{{ t.priority }}</span></td>
              <td><span class="badge-status" :class="'st-' + t.status">{{ formatStatus(t.status) }}</span></td>
              <td>{{ t.messages_count }}</td>
              <td>{{ formatDate(t.created_at) }}</td>
              <td>
                <button class="btn-icon" @click="openThread(t)" title="View thread">
                  <font-awesome-icon icon="comments" />
                </button>
                <button class="btn-icon btn-icon--red" @click="closeTicket(t)" title="Close ticket"
                  v-if="permission?.manage_ticket && t.status !== 'closed'">
                  <font-awesome-icon icon="times-circle" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <!-- Pagination -->
        <div class="pagination-bar" v-if="pagination.last_page > 1">
          <button class="btn-pg" :disabled="pagination.current_page == 1" @click="changePage(pagination.current_page - 1)">Prev</button>
          <span class="pg-info">Page {{ pagination.current_page }} of {{ pagination.last_page }}</span>
          <button class="btn-pg" :disabled="pagination.current_page == pagination.last_page" @click="changePage(pagination.current_page + 1)">Next</button>
        </div>
      </div>
    </div>

    <!-- ── Create Ticket Modal ─────────────────────────────────────────── -->
    <div class="modal fade" id="create_ticket_modal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Raise a Support Ticket</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Subject <span class="text-danger">*</span></label>
                <input v-model="form.subject" type="text" class="form-control" placeholder="Brief description of the issue" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Category <span class="text-danger">*</span></label>
                <select v-model="form.category" class="form-select">
                  <option value="">-- Select --</option>
                  <option value="billing">Billing</option>
                  <option value="technical">Technical</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="account">Account</option>
                  <option value="general">General</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Priority <span class="text-danger">*</span></label>
                <select v-model="form.priority" class="form-select">
                  <option value="">-- Select --</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div class="col-12">
                <label class="form-label">Message <span class="text-danger">*</span></label>
                <textarea v-model="form.message" class="form-control" rows="5" placeholder="Describe the issue in detail…"></textarea>
              </div>
              <div class="col-12">
                <label class="form-label">Attachments <small class="text-muted">(up to 5 files, 10 MB each)</small></label>
                <input type="file" class="form-control" multiple accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                  @change="onAttachmentChange" ref="attachInput" />
                <div v-if="form.attachments.length" class="mt-2 attach-list">
                  <span v-for="(f, i) in form.attachments" :key="i" class="attach-chip">
                    <font-awesome-icon icon="paperclip" /> {{ f.name }}
                    <button class="chip-remove" @click="removeAttachment(i)">&times;</button>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-ghost" data-bs-dismiss="modal">Cancel</button>
            <button class="btn-primary-action" @click="submitTicket()" :disabled="creating">
              <span v-if="creating"><span class="spinner-border spinner-border-sm"></span> Submitting…</span>
              <span v-else>Submit Ticket</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Close Ticket Confirm Modal ───────────────────────────────── -->
    <div class="modal fade" id="close_ticket_modal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered" style="max-width:420px">
        <div class="modal-content">
          <div class="modal-body text-center px-4 pt-4 pb-3">
            <div class="close-warn-icon">
              <font-awesome-icon icon="exclamation-triangle" />
            </div>
            <h5 class="close-warn-title">Close Ticket?</h5>
            <p class="close-warn-body">
              You are about to close ticket
              <strong>{{ ticketToClose?.reference }}</strong>.
              Once closed, no further replies can be sent.
            </p>
          </div>
          <div class="modal-footer justify-content-center border-0 pt-0 pb-4 gap-2">
            <button class="btn-ghost" data-bs-dismiss="modal">Cancel</button>
            <button class="btn-danger-action" @click="confirmClose()" :disabled="closing">
              <span v-if="closing"><span class="spinner-border spinner-border-sm"></span> Closing…</span>
              <span v-else><font-awesome-icon icon="times-circle" /> Close Ticket</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Thread Modal ───────────────────────────────────────────────── -->
    <div class="modal fade" id="thread_modal" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <div>
              <h5 class="modal-title">{{ activeTicket?.subject }}</h5>
              <small class="text-muted">{{ activeTicket?.reference }} &middot; <span class="badge-status" :class="'st-' + activeTicket?.status">{{ formatStatus(activeTicket?.status) }}</span></small>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body thread-body">
            <!-- Ticket meta -->
            <div class="ticket-meta-row">
              <span><b>Category:</b> {{ formatCategory(activeTicket?.category) }}</span>
              <span><b>Priority:</b> <span class="badge-priority" :class="'pri-' + activeTicket?.priority">{{ activeTicket?.priority }}</span></span>
              <span><b>Opened:</b> {{ formatDate(activeTicket?.created_at) }}</span>
              <span v-if="activeTicket?.resolved_at"><b>Resolved:</b> {{ formatDate(activeTicket?.resolved_at) }}</span>
            </div>
            <hr />
            <!-- Messages -->
            <div v-if="threadLoading" class="text-center py-4"><div class="spinner-border text-primary"></div></div>
            <div v-else class="message-list">
              <template v-for="msg in messages" :key="msg.id">

                <!-- History pill: status changes, open/close events, assignments -->
                <div v-if="isHistoryEntry(msg)" class="history-pill">
                  <span v-if="msg.action_type === 'ticket_opened'">
                    <b>{{ msg.sender_name }}</b> opened this ticket
                    <span class="badge-status st-open">Open</span>
                  </span>
                  <span v-else-if="msg.action_type === 'ticket_closed'">
                    <b>{{ msg.sender_name }}</b> closed this ticket
                    <span class="badge-status st-closed">Closed</span>
                  </span>
                  <span v-else-if="msg.action_type === 'assigned'">
                    <b>{{ msg.sender_name }}</b> updated the assignment
                  </span>
                  <span v-else>
                    <b>{{ msg.sender_name }}</b> changed status from
                    <span class="badge-status" :class="'st-' + msg.old_status">{{ formatStatus(msg.old_status) }}</span>
                    to
                    <span class="badge-status" :class="'st-' + msg.new_status">{{ formatStatus(msg.new_status) }}</span>
                  </span>
                  <span class="pill-time">{{ formatDate(msg.created_at) }}</span>
                </div>

                <!-- Regular message bubble -->
                <div v-else class="message-bubble" :class="msg.sender_type === 'admin' ? 'bubble-admin' : 'bubble-user'">
                  <div class="bubble-meta">
                    <span class="bubble-sender">{{ msg.sender_name }}</span>
                    <span class="bubble-time">{{ formatDate(msg.created_at) }}</span>
                  </div>
                  <div class="bubble-text">{{ msg.message }}</div>
                  <div v-if="msg.attachments && msg.attachments.length" class="bubble-attachments">
                    <a v-for="(url, i) in msg.attachments" :key="i" :href="url" target="_blank" class="attach-link">
                      <font-awesome-icon icon="paperclip" /> File {{ i + 1 }}
                    </a>
                  </div>
                </div>

              </template>
              <div v-if="messages.length === 0" class="text-center text-muted py-3">No messages yet.</div>
            </div>
          </div>
          <!-- Awaiting user notice -->
          <div v-if="activeTicket?.status === 'awaiting_user'" class="awaiting-notice">
            <font-awesome-icon icon="clock" />
            The support team is waiting for your response on this ticket.
          </div>

          <!-- Reply box -->
          <div class="reply-box" v-if="activeTicket?.status !== 'closed' && permission?.manage_ticket">
            <textarea v-model="reply.message" class="reply-textarea" rows="3" placeholder="Type your reply…"></textarea>
            <div class="reply-footer">
              <input type="file" class="filter-input" style="flex:1" multiple accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                @change="onReplyAttachChange" ref="replyAttachInput" />
              <button class="btn-primary-action" @click="sendReply()" :disabled="replying">
                <span v-if="replying"><span class="spinner-border spinner-border-sm"></span></span>
                <span v-else><font-awesome-icon icon="paper-plane" /> Send</span>
              </button>
            </div>
            <div v-if="reply.attachments.length" class="attach-list mt-2">
              <span v-for="(f, i) in reply.attachments" :key="i" class="attach-chip">
                <font-awesome-icon icon="paperclip" /> {{ f.name }}
                <button class="chip-remove" @click="removeReplyAttachment(i)">&times;</button>
              </span>
            </div>
          </div>
          <div class="modal-footer" v-else-if="activeTicket?.status === 'closed'">
            <span class="text-muted fst-italic">This ticket is closed.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { reusableMixin } from '../../../lib/reusableMixin';

export default {
  name: "SupportTickets",
  mixins: [reusableMixin],

  data() {
    return {
      loading: false,
      tickets: [],
      pagination: {},
      search: "",
      filterStatus: "",
      currentPage: 1,

      // Create form
      creating: false,
      form: { subject: "", category: "", priority: "", message: "", attachments: [] },

      // Thread
      activeTicket: null,
      messages: [],
      threadLoading: false,
      reply: { message: "", attachments: [] },
      replying: false,

      // Close confirm
      ticketToClose: null,
      closing: false,
    };
  },

  mounted() {
    this.fetchTickets();
  },

  methods: {
    fetchTickets() {
      this.loading = true;
      axios.get("/support/tickets", {
        params: { status: this.filterStatus, search: this.search, page: this.currentPage }
      }).then(res => {
        const d = res?.data?.data;
        this.tickets    = d?.data ?? [];
        this.pagination = { current_page: d?.current_page, last_page: d?.last_page };
      }).catch(() => {}).finally(() => { this.loading = false; });
    },

    changePage(page) {
      this.currentPage = page;
      this.fetchTickets();
    },

    openCreateModal() {
      this.form = { subject: "", category: "", priority: "", message: "", attachments: [] };
      if (this.$refs.attachInput) this.$refs.attachInput.value = "";
      window.$("#create_ticket_modal").modal("show");
    },

    onAttachmentChange(e) {
      this.form.attachments = Array.from(e.target.files);
    },

    removeAttachment(i) {
      this.form.attachments.splice(i, 1);
    },

    submitTicket() {
      if (!this.form.subject || !this.form.category || !this.form.priority || !this.form.message) {
        return this.toast.error("Please fill all required fields.");
      }
      this.creating = true;
      const fd = new FormData();
      fd.append("subject", this.form.subject);
      fd.append("category", this.form.category);
      fd.append("priority", this.form.priority);
      fd.append("message", this.form.message);
      this.form.attachments.forEach((f, i) => fd.append(`attachments[${i}]`, f));

      axios.post("/support/ticket", fd, { headers: { "Content-Type": "multipart/form-data" } })
        .then(res => {
          this.toast.success(res?.data?.message ?? "Ticket created.");
          window.$("#create_ticket_modal").modal("hide");
          this.fetchTickets();
        })
        .catch(err => { this.toast.error(err?.response?.data?.message ?? "Failed to create ticket."); })
        .finally(() => { this.creating = false; });
    },

    openThread(ticket) {
      this.activeTicket  = ticket;
      this.reply         = { message: "", attachments: [] };
      this.messages      = [];
      this.threadLoading = true;
      window.$("#thread_modal").modal("show");
      axios.get(`/support/ticket/${ticket.id}`)
        .then(res => {
          this.activeTicket = res?.data?.data;
          this.messages     = res?.data?.data?.messages ?? [];
        })
        .catch(() => {})
        .finally(() => { this.threadLoading = false; });
    },

    onReplyAttachChange(e) {
      this.reply.attachments = Array.from(e.target.files);
    },

    removeReplyAttachment(i) {
      this.reply.attachments.splice(i, 1);
    },

    sendReply() {
      if (!this.reply.message.trim()) return this.toast.error("Reply cannot be empty.");
      this.replying = true;
      const fd = new FormData();
      fd.append("message", this.reply.message);
      this.reply.attachments.forEach((f, i) => fd.append(`attachments[${i}]`, f));

      axios.post(`/support/ticket/${this.activeTicket.id}/message`, fd, { headers: { "Content-Type": "multipart/form-data" } })
        .then(() => {
          this.reply = { message: "", attachments: [] };
          if (this.$refs.replyAttachInput) this.$refs.replyAttachInput.value = "";
          this.openThread(this.activeTicket); // refresh
        })
        .catch(err => { this.toast.error(err?.response?.data?.message ?? "Failed to send."); })
        .finally(() => { this.replying = false; });
    },

    closeTicket(ticket) {
      this.ticketToClose = ticket;
      window.$("#close_ticket_modal").modal("show");
    },

    confirmClose() {
      if (!this.ticketToClose) return;
      this.closing = true;
      axios.put(`/support/ticket/${this.ticketToClose.id}/close`)
        .then(() => {
          window.$("#close_ticket_modal").modal("hide");
          this.toast.success("Ticket closed.");
          this.fetchTickets();
          if (this.activeTicket?.id === this.ticketToClose.id) {
            this.activeTicket.status = "closed";
          }
          this.ticketToClose = null;
        })
        .catch(err => { this.toast.error(err?.response?.data?.message ?? "Failed."); })
        .finally(() => { this.closing = false; });
    },

    isHistoryEntry(msg) {
      return ['status_change', 'ticket_opened', 'ticket_closed', 'assigned'].includes(msg.action_type);
    },

    formatCategory(cat) {
      const map = { billing: "Billing", technical: "Technical", feature_request: "Feature Request", account: "Account", general: "General" };
      return map[cat] ?? cat;
    },

    formatStatus(s) {
      const map = { open: "Open", in_progress: "In Progress", awaiting_user: "Awaiting User", resolved: "Resolved", closed: "Closed" };
      return map[s] ?? (s ?? "");
    },

    formatDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style scoped>
/* ── Page header ──────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

/* ── Buttons ──────────────────────────────────────────────────────── */
.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; white-space:nowrap; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-ghost:hover { background:#f0f4f8; }
.btn-icon { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:14px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red { color:#dc2626 !important; }
.btn-icon--red:hover { background:#fee2e2 !important; }
.btn-danger-action { display:inline-flex; align-items:center; gap:6px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; white-space:nowrap; }
.btn-danger-action:hover:not(:disabled) { background:#b91c1c; }
.btn-danger-action:disabled { opacity:.6; cursor:not-allowed; }

/* ── Close confirm modal ──────────────────────────────────────────── */
.close-warn-icon  { display:flex; align-items:center; justify-content:center; width:56px; height:56px; border-radius:50%; background:#fef2f2; color:#dc2626; font-size:22px; margin:0 auto 14px; }
.close-warn-title { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 10px; }
.close-warn-body  { font-size:13px; color:#6b7280; margin:0; line-height:1.6; }

/* ── Filter / search ──────────────────────────────────────────────── */
.filter-input  { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; min-width:180px; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* ── Card / table ─────────────────────────────────────────────────── */
.card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.card-body { padding:0; }
.data-table { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; }

/* ── Reference chip ───────────────────────────────────────────────── */
.ref-chip { font-family:monospace; background:#f1f5f9; border:1px solid #e2e8f0; border-radius:4px; padding:2px 8px; font-size:12px; color:#334155; }

/* ── Category badges ──────────────────────────────────────────────── */
.badge-cat { display:inline-block; font-size:11px; font-weight:600; padding:3px 9px; border-radius:20px; text-transform:capitalize; }
.cat-billing         { background:#dbeafe; color:#1d4ed8; }
.cat-technical       { background:#dcfce7; color:#15803d; }
.cat-feature_request { background:#f3e8ff; color:#9333ea; }
.cat-account         { background:#ffedd5; color:#c2410c; }
.cat-general         { background:#f1f5f9; color:#475569; }

/* ── Priority badges ──────────────────────────────────────────────── */
.badge-priority { display:inline-block; font-size:11px; font-weight:600; padding:3px 9px; border-radius:20px; text-transform:capitalize; }
.pri-low    { background:#dcfce7; color:#15803d; }
.pri-medium { background:#fef9c3; color:#b45309; }
.pri-high   { background:#ffedd5; color:#c2410c; }
.pri-urgent { background:#fee2e2; color:#b91c1c; font-weight:700; }

/* ── Status badges ────────────────────────────────────────────────── */
.badge-status { display:inline-block; font-size:11px; font-weight:600; padding:3px 9px; border-radius:20px; }
.st-open           { background:#dbeafe; color:#1d4ed8; }
.st-in_progress    { background:#fef9c3; color:#b45309; }
.st-awaiting_user  { background:#fef3c7; color:#92400e; }
.st-resolved       { background:#dcfce7; color:#15803d; }
.st-closed         { background:#f1f5f9; color:#64748b; }

/* ── Empty state ──────────────────────────────────────────────────── */
.empty-state     { text-align:center; padding:60px 24px; color:#94a3b8; }
.empty-icon-wrap { display:flex; justify-content:center; margin-bottom:16px; }
.empty-icon      { font-size:48px; color:#cbd5e1; }

/* ── Pagination ───────────────────────────────────────────────────── */
.pagination-bar { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px; border-top:1px solid #f0f4f8; }
.btn-pg  { padding:5px 14px; border:1px solid #dde5f0; border-radius:6px; background:#fff; font-size:13px; cursor:pointer; }
.btn-pg:disabled { opacity:.4; cursor:not-allowed; }
.pg-info { font-size:13px; color:#7a90b0; }

/* ── Thread modal ─────────────────────────────────────────────────── */
.thread-body { max-height:460px; overflow-y:auto; padding:16px; background:#f8fafc; }
.ticket-meta-row { display:flex; gap:20px; flex-wrap:wrap; font-size:13px; color:#475569; margin-bottom:12px; background:#fff; border:1px solid #e4eaf2; border-radius:10px; padding:12px 16px; }

.message-list { display:flex; flex-direction:column; gap:14px; }
.message-bubble { max-width:78%; border-radius:14px; padding:12px 16px; }
.bubble-user  { align-self:flex-end; background:#dbeafe; border:1px solid #bfdbfe; }
.bubble-admin { align-self:flex-start; background:#fff; border:1px solid #e4eaf2; }
.bubble-meta  { display:flex; justify-content:space-between; gap:12px; margin-bottom:5px; }
.bubble-sender { font-size:12px; font-weight:700; color:#1a2b45; }
.bubble-time   { font-size:11px; color:#94a3b8; }
.bubble-text   { font-size:13px; line-height:1.65; color:#334155; white-space:pre-wrap; }
.bubble-attachments { margin-top:8px; display:flex; flex-wrap:wrap; gap:6px; }
.attach-link { font-size:12px; color:#2563eb; text-decoration:none; background:#eff6ff; border:1px solid #bfdbfe; padding:3px 10px; border-radius:6px; display:inline-flex; align-items:center; gap:4px; }
.attach-link:hover { background:#dbeafe; }

/* ── Reply box ────────────────────────────────────────────────────── */
.reply-box { padding:16px; border-top:1px solid #e4eaf2; background:#fff; display:flex; flex-direction:column; gap:8px; }
.reply-textarea { width:100%; padding:10px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; resize:vertical; outline:none; box-sizing:border-box; }
.reply-textarea:focus { border-color:#004AAD; }
.reply-footer { display:flex; gap:8px; align-items:center; }

/* ── History pills ────────────────────────────────────────────────── */
.history-pill { display:flex; align-items:center; justify-content:center; gap:6px; padding:7px 14px; background:#f8fafc; border:1px dashed #cbd5e1; border-radius:20px; font-size:12px; color:#64748b; flex-wrap:wrap; text-align:center; }
.pill-time { color:#94a3b8; font-size:11px; }

/* ── Awaiting notice ──────────────────────────────────────────────── */
.awaiting-notice { display:flex; align-items:center; gap:8px; margin:0 16px 0; padding:10px 14px; background:#fef3c7; border:1px solid #fde68a; border-radius:8px; font-size:13px; color:#92400e; font-weight:600; }

/* ── Attachments ──────────────────────────────────────────────────── */
.attach-list { display:flex; flex-wrap:wrap; gap:6px; }
.attach-chip { font-size:12px; background:#f1f5f9; border:1px solid #e2e8f0; border-radius:20px; padding:3px 10px; display:inline-flex; align-items:center; gap:4px; color:#334155; }
.chip-remove { background:none; border:none; font-size:14px; cursor:pointer; padding:0 2px; color:#94a3b8; line-height:1; }
.chip-remove:hover { color:#dc2626; }
</style>
